import { useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from '../../../../state';
import {
  SalesTaxSearchInput,
  closeModal,
  searchSalesTaxCodes,
} from '../../../../state/salesTaxSearchModalSlice';
import DraggablePaper from '../../DraggablePaper';
import Table, { INITIAL_PAGE_SIZE } from './Table';

export default function SalesTaxSearchModal() {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Modals.SalesTaxSearch',
  });
  const {
    selectedTaxCodes,
    multiSelect,
    openSalesTaxSearchModal,
    loading,
    onImport,
    onCancel,
  } = useSelector(({ salesTaxSearchModalSlice }) => salesTaxSearchModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
    if (onCancel) onCancel();
  };

  const importTaxCode = () => {
    dispatch(closeModal());
    if (onImport) onImport(selectedTaxCodes);
  };

  useEffect(() => {
    if (!openSalesTaxSearchModal || team === 'init' || team === 'loading') {
      return;
    }

    const payload: SalesTaxSearchInput = {
      query: '',
      searchBy: 'codeName',
      size: INITIAL_PAGE_SIZE,
      page: 0,
    };
    dispatch(searchSalesTaxCodes(payload));
  }, [dispatch, openSalesTaxSearchModal, team]);

  return (
    <Dialog
      open={openSalesTaxSearchModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {t('Browse Sales Tax')}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height={700}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <Box sx={{ height: 700, width: '100%' }}>
            <Table multiSelect={multiSelect} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          width="100%"
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
          }}
        >
          <Button
            disabled={loading}
            color="primary"
            startIcon={<ArrowBackIosNewIcon />}
            onClick={handleClose}
            sx={{ justifySelf: 'start' }}
          >
            {t('Back')}
          </Button>
          <Button
            disabled={loading || selectedTaxCodes.length === 0}
            variant="contained"
            color="primary"
            onClick={importTaxCode}
            sx={{ width: 120 }}
          >
            {loading ? <CircularProgress size={20} /> : t('Select')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
